import dayjs from 'dayjs';
import { AdminActionType, EntityType } from './types';
import { dateFormat, timeFormat } from '../../../common';
import { getQmListRoleShortLabel } from '../../../common/qmRoles';

export const formatActionName = (action: string) => {
  // Shorten common action patterns
  const actionMap: { [key: string]: string } = {
    [AdminActionType.MANIFEST_DELETE]: 'Delete Manifest',
    [AdminActionType.MANIFEST_COMPLETE]: 'Complete Manifest',
    [AdminActionType.SSCC_ADD_TO_MANIFEST]: 'Add SSCC',
    [AdminActionType.SSCC_REMOVE_FROM_MANIFEST]: 'Remove SSCC',
    [AdminActionType.SSCC_DELETE]: 'Delete SSCC',
    [AdminActionType.SSCC_COMPLETE]: 'Complete SSCC',
    [AdminActionType.ASN_ASSIGN_TO_SSCC]: 'Assign ASN',
    [AdminActionType.ASN_REMOVE_FROM_SSCC]: 'Remove ASN',
    [AdminActionType.QM_ROLE_SET_NO_ACCESS]: 'QM Role: Own Only',
    [AdminActionType.QM_ROLE_SET_READ_ONLY]: 'QM Role: Read All',
    [AdminActionType.QM_ROLE_SET_EDITABLE]: 'QM Role: Write All',
    [AdminActionType.QM_NEW_DASHBOARD_ENABLE]: 'QM Toggle: Enabled',
    [AdminActionType.QM_NEW_DASHBOARD_DISABLE]: 'QM Toggle: Disabled',
    [AdminActionType.LOCATION_CREATE]: 'Create Location',
    [AdminActionType.LOCATION_UPDATE]: 'Update Location',
    [AdminActionType.PRINTER_CREATE]: 'Create Printer',
    [AdminActionType.PRINTER_UPDATE]: 'Update Printer'
  };

  return actionMap[action] || action;
};

export const getActionColor = (action: string) => {
  if (action.includes('Complete') || action.includes('Grant')) {
    return { bg: '#e8f5e9', text: '#2e7d32', border: '#66bb6a' }; // Green for positive actions
  } else if (action.includes('Remove') || action.includes('Delete') || action.includes('Revoke')) {
    return { bg: '#ffebee', text: '#c62828', border: '#ef5350' }; // Red for destructive actions
  } else if (action.includes('Status Change') || action.includes('Unstage')) {
    return { bg: '#fff3e0', text: '#e65100', border: '#ff9800' }; // Orange for status changes
  } else if (action.includes('Add') || action.includes('Assign')) {
    return { bg: '#e3f2fd', text: '#1565c0', border: '#42a5f5' }; // Blue for additions
  } else {
    return { bg: 'rgba(0, 0, 0, 0.03)', text: 'inherit', border: 'rgba(0, 0, 0, 0.08)' }; // Default gray
  }
};

export const getEntityBadgeColor = (entityType: EntityType) => {
  switch (entityType) {
    case EntityType.Manifest:
      return '#1976d2';
    case EntityType.SSCC:
      return '#9c27b0';
    case EntityType.ASN:
      return '#00796b';
    case EntityType.Access:
      return '#f57c00';
    case EntityType.Location:
      return '#5d4037';
    case EntityType.Printer:
      return '#455a64';
    default:
      return '#757575';
  }
};

export const formatTimestamp = (timestamp: string) => {
  const date = dayjs(timestamp);
  const now = dayjs();
  const diffMinutes = now.diff(date, 'minute');
  const diffHours = now.diff(date, 'hour');
  const diffDays = now.diff(date, 'day');

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return `${date.format(dateFormat)} ${date.format(timeFormat)}`;
};

export const formatAction = (action: string): AdminActionType => {
  switch (action) {
    case 'Manifest Delete':
      return AdminActionType.MANIFEST_DELETE;
    case 'Manifest Complete':
      return AdminActionType.MANIFEST_COMPLETE;
    case 'SSCC Add to Manifest':
      return AdminActionType.SSCC_ADD_TO_MANIFEST;
    case 'SSCC Remove from Manifest':
      return AdminActionType.SSCC_REMOVE_FROM_MANIFEST;
    case 'SSCC Delete':
      return AdminActionType.SSCC_DELETE;
    case 'SSCC Complete':
      return AdminActionType.SSCC_COMPLETE;
    case 'ASN Assign to SSCC':
      return AdminActionType.ASN_ASSIGN_TO_SSCC;
    case 'ASN Remove from SSCC':
      return AdminActionType.ASN_REMOVE_FROM_SSCC;
    case 'Grant Access':
      return AdminActionType.ADMIN_PANEL_GRANT_ACCESS;
    case 'Revoke Access':
      return AdminActionType.ADMIN_PANEL_REVOKE_ACCESS;
    case 'QM Role Set No Access':
    case 'QM Dashboard Role Set No Access':
      return AdminActionType.QM_ROLE_SET_NO_ACCESS;
    case 'QM Role Set Read Only':
    case 'QM Dashboard Role Set Read Only':
      return AdminActionType.QM_ROLE_SET_READ_ONLY;
    case 'QM Role Set Editable':
    case 'QM Dashboard Role Set Editable':
      return AdminActionType.QM_ROLE_SET_EDITABLE;
    case 'QM New Dashboard Enabled':
      return AdminActionType.QM_NEW_DASHBOARD_ENABLE;
    case 'QM New Dashboard Disabled':
      return AdminActionType.QM_NEW_DASHBOARD_DISABLE;
    case 'CreateLocationAction':
    case 'Create Location':
      return AdminActionType.LOCATION_CREATE;
    case 'UpdateLocationAction':
    case 'Update Location':
      return AdminActionType.LOCATION_UPDATE;
    case 'CreatePrinterAction':
    case 'Create Printer':
      return AdminActionType.PRINTER_CREATE;
    case 'UpdatePrinterAction':
    case 'Update Printer':
      return AdminActionType.PRINTER_UPDATE;
    default:
      return AdminActionType.UNKNOWN;
  }
};

/**
 * Generates a human-readable summary of changes
 */
export const generateChangesSummary = (before: any, after: any, truncate: boolean = true): string => {
  if (!before && !after) return 'No changes recorded';

  const changes: string[] = [];
  const beforeState = before || {};
  const afterState = after || {};

  const hasOwn = (obj: any, key: string) => obj && Object.prototype.hasOwnProperty.call(obj, key);
  const isPrinterPayload = (obj: any) => hasOwn(obj, 'printerIP') || hasOwn(obj, 'printerPort');
  const isLocationPayload = (obj: any) =>
    hasOwn(obj, 'parentLocationId') || hasOwn(obj, 'suburb') || hasOwn(obj, 'postcode') || hasOwn(obj, 'alias');

  const listToString = (values: string[]) => {
    if (!truncate) return values.join(', ');
    if (values.length <= 3) return values.join(', ');
    return `${values.slice(0, 3).join(', ')} +${values.length - 3} more`;
  };

  // Access settings changes
  if (beforeState?.adminAccess !== undefined && afterState?.adminAccess !== undefined) {
    if (beforeState.adminAccess !== afterState.adminAccess) {
      return `Access: ${beforeState.adminAccess ? '✅' : '❌'} -> ${afterState.adminAccess ? '✅' : '❌'}`;
    }
  }

  // QM dashboard role changes
  const hasBeforeQmListRole = hasOwn(beforeState, 'qmListRole');
  const hasAfterQmListRole = hasOwn(afterState, 'qmListRole');
  if (hasBeforeQmListRole || hasAfterQmListRole) {
    const beforeRole = hasBeforeQmListRole ? beforeState.qmListRole : null;
    const afterRole = hasAfterQmListRole ? afterState.qmListRole : null;

    if (beforeRole !== afterRole) {
      const beforeRoleLabel = hasBeforeQmListRole ? getQmListRoleShortLabel(beforeRole) : 'Not Set';
      const afterRoleLabel = hasAfterQmListRole ? getQmListRoleShortLabel(afterRole) : 'Not Set';
      return `QM Role: ${beforeRoleLabel} → ${afterRoleLabel}`;
    }
  }

  // QM new dashboard global toggle changes
  if (hasOwn(beforeState, 'qmNewDashboardEnabled') || hasOwn(afterState, 'qmNewDashboardEnabled')) {
    const beforeEnabled = Boolean(beforeState?.qmNewDashboardEnabled);
    const afterEnabled = Boolean(afterState?.qmNewDashboardEnabled);
    if (beforeEnabled !== afterEnabled) {
      return `QM New Dashboard: ${beforeEnabled ? 'Enabled' : 'Disabled'} → ${afterEnabled ? 'Enabled' : 'Disabled'}`;
    }
  }

  // Entity identity/context
  const entityType = afterState.entityType || beforeState.entityType;
  const entityId = afterState.entityId || beforeState.entityId;
  const entityLabel = entityType && entityId ? `${entityType} ${entityId}` : '';

  // Printer create/update details
  const printerRecord = isPrinterPayload(afterState) ? afterState : isPrinterPayload(beforeState) ? beforeState : null;
  if (printerRecord) {
    const printerId = afterState?.id || beforeState?.id || 'Unknown';
    const printerName = afterState?.name || beforeState?.name || 'Unknown';
    const isCreate = !beforeState || Object.keys(beforeState).length === 0;

    if (isCreate && afterState && Object.keys(afterState).length > 0) {
      return `Printer ${printerName} (${printerId}) created; IP: ${afterState.printerIP || 'N/A'}; Port: ${afterState.printerPort || 'N/A'}; Disabled: ${afterState.isDisabled ? 'Yes' : 'No'}`;
    }

    const printerCoreFields = ['printerIP', 'printerPort', 'isDisabled'];
    const changedPrinterCoreFields = printerCoreFields.filter((key) => beforeState?.[key] !== afterState?.[key]);

    if (changedPrinterCoreFields.length > 0 || beforeState?.name !== afterState?.name) {
      const printerFieldDetails = printerCoreFields
        .map((key) => `${key}: ${beforeState?.[key] ?? 'N/A'} → ${afterState?.[key] ?? 'N/A'}`)
        .join('; ');

      if (beforeState?.name !== afterState?.name) {
        return `Printer ${printerName} (${printerId}) updated; name: ${beforeState?.name ?? 'N/A'} → ${afterState?.name ?? 'N/A'}; ${printerFieldDetails}`;
      }

      return `Printer ${printerName} (${printerId}) updated; ${printerFieldDetails}`;
    }
  }

  // Location create/update details
  const locationRecord = isLocationPayload(afterState)
    ? afterState
    : isLocationPayload(beforeState)
      ? beforeState
      : null;
  if (locationRecord) {
    const locationId = afterState?.id || beforeState?.id || 'Unknown';
    const locationName = afterState?.name || beforeState?.name || 'Unknown';
    const isCreate = !beforeState || Object.keys(beforeState).length === 0;
    const locationFields = [
      'type',
      'name',
      'alias',
      'address',
      'suburb',
      'state',
      'postcode',
      'contactName',
      'contactPhone',
      'isDisabled',
      'parentLocationId'
    ];

    if (isCreate && afterState && Object.keys(afterState).length > 0) {
      const locationCreateDetails = locationFields.map((key) => `${key}: ${afterState?.[key] ?? 'N/A'}`).join('; ');
      return `Location ${locationName} (${locationId}) created; ${locationCreateDetails}`;
    }

    const changedLocationFields = locationFields.filter((key) => beforeState?.[key] !== afterState?.[key]);
    if (changedLocationFields.length > 0) {
      const locationUpdateDetails = locationFields
        .map((key) => `${key}: ${beforeState?.[key] ?? 'N/A'} → ${afterState?.[key] ?? 'N/A'}`)
        .join('; ');
      return `Location ${locationName} (${locationId}) updated; ${locationUpdateDetails}`;
    }
  }

  // Manifest delete summary with trip/leg context
  if (entityType === EntityType.Manifest && afterState?.deleted === true) {
    const manifestId = afterState?.manifestId || beforeState?.manifestId || entityId || 'Unknown';
    const tripReference =
      afterState?.tripReference || beforeState?.tripReference || afterState?.tripId || beforeState?.tripId || 'N/A';
    const legNumber = Number.isInteger(afterState?.legIndex)
      ? afterState.legIndex + 1
      : Number.isInteger(beforeState?.legIndex)
        ? beforeState.legIndex + 1
        : null;
    const legDisplay = legNumber !== null ? legNumber : afterState?.legId || beforeState?.legId || 'N/A';

    const receiveManifestDisplay = afterState?.inventoryTransfer ? `; Receive Manifest: YES.` : '';

    return `Manifest ${manifestId} was deleted from Trip ${tripReference}, Leg ${legDisplay}${receiveManifestDisplay}`;
  }

  // Manifest completion summary with trip/leg context and ASN status changes
  if (entityType === EntityType.Manifest && beforeState?.status && afterState?.status) {
    const manifestId = afterState?.manifestId || beforeState?.manifestId || entityId || 'Unknown';
    const tripReference =
      afterState?.tripReference || beforeState?.tripReference || afterState?.tripId || beforeState?.tripId || 'N/A';
    const legNumber = Number.isInteger(afterState?.legIndex)
      ? afterState.legIndex + 1
      : Number.isInteger(beforeState?.legIndex)
        ? beforeState.legIndex + 1
        : null;
    const legDisplay = legNumber !== null ? legNumber : afterState?.legId || beforeState?.legId || 'N/A';

    const asnStatusChanges = Array.isArray(afterState?.asnStatusChanges) ? afterState.asnStatusChanges : [];

    if (asnStatusChanges.length > 0) {
      const asnChangeEntries = asnStatusChanges
        .map((item: any) => `${item?.asn || 'N/A'}: ${item?.beforeStatus || 'N/A'} → ${item?.afterStatus || 'N/A'}`)
        .filter((value: string) => value.trim().length > 0);

      const asnChangesDisplay = !truncate
        ? asnChangeEntries.join(', ')
        : asnChangeEntries.length <= 8
          ? asnChangeEntries.join(', ')
          : `${asnChangeEntries.slice(0, 8).join(', ')} +${asnChangeEntries.length - 8} more`;

      const receiveManifestDisplay = afterState?.inventoryTransfer ? `; Receive Manifest: YES.` : '';

      return `Manifest ${manifestId} in Trip ${tripReference}, Leg ${legDisplay}: Status ${beforeState.status} → ${afterState.status}; ASN status changes: ${asnChangesDisplay}${receiveManifestDisplay}`;
    }
  }

  // SSCC delete summary with trip/leg/manifest context, SSCC status change and ASN status changes
  if (entityType === EntityType.SSCC && afterState?.deleted === true) {
    const ssccId = afterState?.ssccId || beforeState?.ssccId || entityId || 'Unknown';
    const isUnassigned = Boolean(afterState?.isUnassigned ?? beforeState?.isUnassigned);
    const manifestId = afterState?.manifestId || beforeState?.manifestId || 'N/A';
    const tripReference =
      afterState?.tripReference || beforeState?.tripReference || afterState?.tripId || beforeState?.tripId || 'N/A';
    const legNumber = Number.isInteger(afterState?.legIndex)
      ? afterState.legIndex + 1
      : Number.isInteger(beforeState?.legIndex)
        ? beforeState.legIndex + 1
        : null;
    const legDisplay = legNumber !== null ? legNumber : afterState?.legId || beforeState?.legId || 'N/A';

    const ssccStatusDisplay =
      beforeState?.status && afterState?.status
        ? `${beforeState.status} → ${afterState.status}`
        : afterState?.status || beforeState?.status || 'N/A';

    const asnStatusChangesSource = Array.isArray(afterState?.asnStatusChanges)
      ? afterState.asnStatusChanges
      : Array.isArray(beforeState?.asnStatusChanges)
        ? beforeState.asnStatusChanges
        : [];

    const asnStatusChangesEntries = asnStatusChangesSource
      .map((item: any) => `${item?.asn || 'N/A'}: ${item?.beforeStatus || 'N/A'} → ${item?.afterStatus || 'N/A'}`)
      .filter((value: string) => value.trim().length > 0);

    const asnStatusChangesDisplay =
      asnStatusChangesEntries.length === 0
        ? 'None'
        : !truncate
          ? asnStatusChangesEntries.join(', ')
          : asnStatusChangesEntries.length <= 8
            ? asnStatusChangesEntries.join(', ')
            : `${asnStatusChangesEntries.slice(0, 8).join(', ')} +${asnStatusChangesEntries.length - 8} more`;

    const receiveManifestDisplay = afterState?.inventoryTransfer ? `; Receive Manifest: YES` : '';

    const hasCoupaAsnAvailabilityFlag =
      afterState?.updateAsnToAvailable !== undefined || beforeState?.updateAsnToAvailable !== undefined;
    const markAsnAvailableInCoupa = Boolean(
      afterState?.updateAsnToAvailable !== undefined
        ? afterState.updateAsnToAvailable
        : beforeState?.updateAsnToAvailable
    );
    const coupaAvailabilityDisplay = hasCoupaAsnAvailabilityFlag
      ? `; Mark ASN available in Coupa: ${markAsnAvailableInCoupa ? 'YES' : 'NO'}`
      : '';

    if (isUnassigned) {
      return `Unassigned SSCC ${ssccId} was cancelled; SSCC status: ${ssccStatusDisplay}; ASN status changes: ${asnStatusChangesDisplay}${coupaAvailabilityDisplay}`;
    }

    return `SSCC ${ssccId} was removed from Trip ${tripReference}, Leg ${legDisplay}, Manifest ${manifestId}; SSCC status: ${ssccStatusDisplay}; ASN status changes: ${asnStatusChangesDisplay}${receiveManifestDisplay}${coupaAvailabilityDisplay}`;
  }

  // SSCC add-to-manifest summary with trip/leg/manifest context, SSCC status change and ASN status changes
  if (
    entityType === EntityType.SSCC &&
    (afterState?.addedToManifest === true || beforeState?.addedToManifest === true)
  ) {
    const ssccId = afterState?.ssccId || beforeState?.ssccId || entityId || 'Unknown';
    const manifestId = afterState?.manifestId || beforeState?.manifestId || 'N/A';
    const tripReference =
      afterState?.tripReference || beforeState?.tripReference || afterState?.tripId || beforeState?.tripId || 'N/A';
    const legNumber = Number.isInteger(afterState?.legIndex)
      ? afterState.legIndex + 1
      : Number.isInteger(beforeState?.legIndex)
        ? beforeState.legIndex + 1
        : null;
    const legDisplay = legNumber !== null ? legNumber : afterState?.legId || beforeState?.legId || 'N/A';

    const ssccStatusDisplay =
      beforeState?.status && afterState?.status
        ? `${beforeState.status} → ${afterState.status}`
        : afterState?.status || beforeState?.status || 'N/A';

    const asnStatusChangesSource = Array.isArray(afterState?.asnStatusChanges)
      ? afterState.asnStatusChanges
      : Array.isArray(beforeState?.asnStatusChanges)
        ? beforeState.asnStatusChanges
        : [];

    const asnStatusChangesEntries = asnStatusChangesSource
      .map((item: any) => `${item?.asn || 'N/A'}: ${item?.beforeStatus || 'N/A'} → ${item?.afterStatus || 'N/A'}`)
      .filter((value: string) => value.trim().length > 0);

    const asnStatusChangesDisplay =
      asnStatusChangesEntries.length === 0
        ? 'None'
        : !truncate
          ? asnStatusChangesEntries.join(', ')
          : asnStatusChangesEntries.length <= 8
            ? asnStatusChangesEntries.join(', ')
            : `${asnStatusChangesEntries.slice(0, 8).join(', ')} +${asnStatusChangesEntries.length - 8} more`;

    const ssccWeightRaw =
      afterState?.ssccWeight !== undefined && afterState?.ssccWeight !== null
        ? afterState.ssccWeight
        : beforeState?.ssccWeight;
    const ssccWeightDisplay =
      ssccWeightRaw !== undefined && ssccWeightRaw !== null && String(ssccWeightRaw).trim().length > 0
        ? `; Weight: ${ssccWeightRaw} kg`
        : '';

    const receiveManifestDisplay = afterState?.inventoryTransfer ? `; Receive Manifest: YES` : '; Receive Manifest: NO';

    return `SSCC ${ssccId} was added to Trip ${tripReference}, Leg ${legDisplay}, Manifest ${manifestId}; SSCC status: ${ssccStatusDisplay}; ASN status changes: ${asnStatusChangesDisplay}${ssccWeightDisplay}${receiveManifestDisplay}`;
  }

  // SSCC completion summary with trip/leg context and ASN status changes
  if (
    entityType === EntityType.SSCC &&
    Array.isArray(afterState?.asnStatusChanges) &&
    afterState.asnStatusChanges.length > 0
  ) {
    const ssccId = afterState?.ssccId || beforeState?.ssccId || entityId || 'Unknown';
    const tripReference =
      afterState?.tripReference || beforeState?.tripReference || afterState?.tripId || beforeState?.tripId || null;
    const legNumber = Number.isInteger(afterState?.legIndex)
      ? afterState.legIndex + 1
      : Number.isInteger(beforeState?.legIndex)
        ? beforeState.legIndex + 1
        : null;

    const asnChangeEntries = afterState.asnStatusChanges
      .map((item: any) => `${item?.asn || 'N/A'}: ${item?.beforeStatus || 'N/A'} → ${item?.afterStatus || 'N/A'}`)
      .filter((value: string) => value.trim().length > 0);

    const asnChangesDisplay = !truncate
      ? asnChangeEntries.join(', ')
      : asnChangeEntries.length <= 8
        ? asnChangeEntries.join(', ')
        : `${asnChangeEntries.slice(0, 8).join(', ')} +${asnChangeEntries.length - 8} more`;

    const ssccStatusChange =
      beforeState?.status && afterState?.status
        ? `Status ${beforeState.status} → ${afterState.status}`
        : afterState?.status
          ? `Status ${afterState.status}`
          : null;

    const ssccWeightRaw =
      afterState?.ssccWeight !== undefined && afterState?.ssccWeight !== null
        ? afterState.ssccWeight
        : beforeState?.ssccWeight;
    const ssccWeightDisplay =
      ssccWeightRaw !== undefined && ssccWeightRaw !== null && String(ssccWeightRaw).trim().length > 0
        ? `; Weight: ${ssccWeightRaw} kg`
        : '';

    const contextParts = [tripReference ? `Trip ${tripReference}` : '', legNumber !== null ? `Leg ${legNumber}` : '']
      .filter(Boolean)
      .join(', ');

    const receiveManifestDisplay = afterState?.inventoryTransfer ? `; Receive Manifest: YES` : '';

    if (contextParts) {
      return `SSCC ${ssccId} completed in ${contextParts}${ssccStatusChange ? `; ${ssccStatusChange}` : ''}${ssccWeightDisplay}; ASN status changes: ${asnChangesDisplay}${receiveManifestDisplay}`;
    }

    return `SSCC ${ssccId} completed${ssccStatusChange ? `; ${ssccStatusChange}` : ''}${ssccWeightDisplay}; ASN status changes: ${asnChangesDisplay}${receiveManifestDisplay}`;
  }

  // ASN delete summary with trip/leg/manifest/sscc context
  if (entityType === EntityType.ASN && afterState?.deleted === true) {
    const asnId = afterState?.asnId || beforeState?.asnId || entityId || 'Unknown';
    const tripReference =
      afterState?.tripReference || beforeState?.tripReference || afterState?.tripId || beforeState?.tripId || 'N/A';
    const legNumber = Number.isInteger(afterState?.legIndex)
      ? afterState.legIndex + 1
      : Number.isInteger(beforeState?.legIndex)
        ? beforeState.legIndex + 1
        : null;
    const legDisplay = legNumber !== null ? legNumber : afterState?.legId || beforeState?.legId || 'N/A';
    const manifestId = afterState?.manifestId || beforeState?.manifestId || 'N/A';
    const ssccId = afterState?.ssccId || beforeState?.ssccId || 'N/A';
    const isPriorityShipping = Boolean(afterState?.priorityShipping ?? beforeState?.priorityShipping);
    const receiveManifestChecked = Boolean(afterState?.receiveManifestChecked ?? beforeState?.receiveManifestChecked);
    const priorityReceiveManifestNote =
      isPriorityShipping && receiveManifestChecked ? '; Priority Shipping: Receive Manifest checked' : '';

    const receiveManifestDisplay = afterState?.inventoryTransfer ? `; Receive Manifest: YES` : '';

    return `ASN ${asnId} was deleted from Trip ${tripReference}, Leg ${legDisplay}, Manifest ${manifestId}, SSCC ${ssccId}${priorityReceiveManifestNote}${receiveManifestDisplay}`;
  }

  // ASN assignment summary with SSCC context and optional trip/leg/manifest details
  if (entityType === EntityType.ASN && (afterState?.added === true || beforeState?.added === true)) {
    const asnId = afterState?.asnId || beforeState?.asnId || entityId || 'Unknown';
    const ssccId = afterState?.ssccId || beforeState?.ssccId || 'N/A';

    const tripReference =
      afterState?.tripReference || beforeState?.tripReference || afterState?.tripId || beforeState?.tripId;
    const legNumber = Number.isInteger(afterState?.legIndex)
      ? afterState.legIndex + 1
      : Number.isInteger(beforeState?.legIndex)
        ? beforeState.legIndex + 1
        : null;
    const legDisplay = legNumber !== null ? legNumber : afterState?.legId || beforeState?.legId;
    const manifestId = afterState?.manifestId || beforeState?.manifestId;

    const contextParts = [
      tripReference ? `Trip ${tripReference}` : '',
      legDisplay ? `Leg ${legDisplay}` : '',
      manifestId ? `Manifest ${manifestId}` : ''
    ].filter(Boolean);

    const asnStatusChange =
      beforeState?.status && afterState?.status
        ? `${beforeState.status} → ${afterState.status}`
        : afterState?.status
          ? `${afterState.status}`
          : beforeState?.status
            ? `${beforeState.status}`
            : null;

    const receiveManifestDisplay = afterState?.inventoryTransfer ? `; Receive Manifest: YES` : '; Receive Manifest: NO';

    if (contextParts.length > 0) {
      return `ASN ${asnId} was added to SSCC ${ssccId} in ${contextParts.join(', ')}${asnStatusChange ? `; ASN status: ${asnStatusChange}` : ''}${receiveManifestDisplay}`;
    }

    return `ASN ${asnId} was added to SSCC ${ssccId}${asnStatusChange ? `; ASN status: ${asnStatusChange}` : ''}${receiveManifestDisplay}`;
  }

  // Status transitions
  if (
    beforeState?.status !== undefined &&
    afterState?.status !== undefined &&
    beforeState.status !== afterState.status
  ) {
    changes.push(`Status: ${beforeState.status} → ${afterState.status}`);
  }

  // Manifest reassignment
  if (beforeState?.manifestId !== afterState?.manifestId) {
    if (beforeState?.manifestId && afterState?.manifestId) {
      changes.push(`Manifest: ${beforeState.manifestId} → ${afterState.manifestId}`);
    } else if (afterState?.manifestId) {
      changes.push(`Added to Manifest: ${afterState.manifestId}`);
    } else if (beforeState?.manifestId) {
      changes.push(`Removed from Manifest: ${beforeState.manifestId}`);
    }
  }

  // SSCC list diffs (supports both ssccs and ssccIds shapes)
  const beforeSSCCsRaw = beforeState?.ssccIds || beforeState?.ssccs || [];
  const afterSSCCsRaw = afterState?.ssccIds || afterState?.ssccs || [];
  const beforeSSCCs = new Set<string>(
    beforeSSCCsRaw
      .map((value: any) => (typeof value === 'string' ? value : value?.ssccId))
      .filter((value: unknown): value is string => Boolean(value))
  );
  const afterSSCCs = new Set<string>(
    afterSSCCsRaw
      .map((value: any) => (typeof value === 'string' ? value : value?.ssccId))
      .filter((value: unknown): value is string => Boolean(value))
  );

  const addedSSCCs: string[] = Array.from(afterSSCCs).filter((id) => !beforeSSCCs.has(id));
  const removedSSCCs: string[] = Array.from(beforeSSCCs).filter((id) => !afterSSCCs.has(id));

  if (addedSSCCs.length > 0) {
    changes.push(`Added SSCC(s): ${listToString(addedSSCCs)}`);
  }
  if (removedSSCCs.length > 0) {
    changes.push(`Removed SSCC(s): ${listToString(removedSSCCs)}`);
  }

  // ASN bulk delete support (legacy shapes)
  if (afterState?.asnsRemovedList || afterState?.asnsRemovedCount || beforeState?.asnsToBeRemoved) {
    const removedCount = afterState?.asnsRemovedCount || beforeState?.asnsToBeRemoved?.length || 0;
    const removedList = afterState?.asnsRemovedList || beforeState?.asnsToBeRemoved?.map((a: any) => a.asn) || [];

    if (removedCount > 0) {
      const ssccId = beforeState?.ssccId || afterState?.ssccId || 'Unknown';
      changes.push(`Removed ${removedCount} ASN(s) from SSCC ${ssccId}`);
      if (removedList.length > 0) {
        changes.push(`ASNs: ${listToString(removedList)}`);
      }
    }
  }

  // Single ASN operations
  if (beforeState?.asnId && afterState?.asnId && beforeState.asnId !== afterState.asnId) {
    changes.push(`ASN: ${beforeState.asnId} → ${afterState.asnId}`);
  } else if (afterState?.asnId || beforeState?.asnId) {
    changes.push(`ASN: ${afterState?.asnId || beforeState?.asnId}`);
  }

  // Operation flags
  if (afterState?.deleted === true && beforeState?.deleted !== true) {
    changes.push('Deleted');
  }
  if (afterState?.added === true && beforeState?.added !== true) {
    changes.push('Added');
  }
  if (
    afterState?.isUnassigned !== undefined &&
    beforeState?.isUnassigned !== undefined &&
    beforeState.isUnassigned !== afterState.isUnassigned
  ) {
    changes.push(`Unassigned: ${beforeState.isUnassigned ? 'Yes' : 'No'} → ${afterState.isUnassigned ? 'Yes' : 'No'}`);
  }

  // Coupa-related hints
  if (afterState?.inventoryTransfer) {
    changes.push(`Receive Manifest: ${afterState.inventoryTransfer ? 'YES' : 'NO'}`);
  }

  if (changes.length > 0) {
    return entityLabel ? `${entityLabel}: ${changes.join('; ')}` : changes.join('; ');
  }

  if (afterState?.changesSummary || beforeState?.changesSummary) {
    return afterState?.changesSummary || beforeState?.changesSummary;
  }

  return 'State changed';
};
